import { useCallback } from 'react';
import {
  InvalidateOptions,
  InvalidateQueryFilters,
  useQueryClient,
} from 'react-query';
import { QueryFilters } from 'react-query/types/core/utils';
import {
  BankTransactionsCacheKeys,
  ExpensesCacheKeys,
  IntegrationsCacheKeys,
  RevenuesCacheKeys,
  TaxesCacheKeys,
  UserCacheKeys,
} from 'types/cacheKeys.types';
import { SubWorld, World } from 'types/shared/worlds';

const expensesPageKeys = [
  ExpensesCacheKeys.ExpensesToReview,
  ExpensesCacheKeys.AllExpenses,
  ExpensesCacheKeys.CurrentPeriodDeductibleAmount,
  ExpensesCacheKeys.CurrentYearDeductibleAmount,
  ExpensesCacheKeys.HasExpenses,
  ExpensesCacheKeys.ExpensesFilters,
];

const revenuesPageKeys = [
  RevenuesCacheKeys.RevenuesToReview,
  RevenuesCacheKeys.AllRevenues,
  RevenuesCacheKeys.CurrentYearIncomeAmount,
  RevenuesCacheKeys.CurrentPeriodIncomeAmount,
  RevenuesCacheKeys.HasRevenues,
  RevenuesCacheKeys.RevenuesFilters,
  RevenuesCacheKeys.LatestRevenues,
];

const bankTransactionsPageKeys = [
  BankTransactionsCacheKeys.TransactionsList,
  BankTransactionsCacheKeys.BankTransactionsFilters,
  BankTransactionsCacheKeys.CashAvailableFactors,
];

const revenuesTransactionsTaxes = [
  ...revenuesPageKeys,
  ...bankTransactionsPageKeys,
  TaxesCacheKeys.Taxes,
];

// Assign affected query keys that need to be invalidated to each world
const mapper = {
  [World.BANK]: [
    ...expensesPageKeys,
    ...revenuesPageKeys,
    TaxesCacheKeys.Taxes,
  ],
  [World.EXPENSES]: [...bankTransactionsPageKeys, TaxesCacheKeys.Taxes],
  [World.REVENUES]: [
    ...revenuesPageKeys,
    ...bankTransactionsPageKeys,
    TaxesCacheKeys.Taxes,
  ],
  [SubWorld.CLIENTS]: [
    RevenuesCacheKeys.RevenueClientFieldOptions,
    RevenuesCacheKeys.RevenuesFilters,
    RevenuesCacheKeys.RecurringRevenues,
    RevenuesCacheKeys.HasAnyClients,
  ],
  [World.TAXES]: [...expensesPageKeys, ...revenuesPageKeys],
  [SubWorld.QUOTES]: [
    RevenuesCacheKeys.QuotesFilters,
    RevenuesCacheKeys.RevenuesQuotes,
    RevenuesCacheKeys.HasQuotes,
  ],
  assetExpenses: [
    ...expensesPageKeys,
    ...bankTransactionsPageKeys,
    TaxesCacheKeys.Taxes,
  ],
  taxChecks: [...expensesPageKeys, ...revenuesPageKeys],
  assetRevenues: revenuesTransactionsTaxes,
  revenueUpdateStatusEvent: revenuesTransactionsTaxes,
  germanCarsAddRevenue: revenuesTransactionsTaxes,
  recurringInvoices: [
    RevenuesCacheKeys.RecurringRevenues,
    ...revenuesTransactionsTaxes,
  ],
  expensesIntro: expensesPageKeys,
  globalExpense: [
    ...expensesPageKeys,
    ...bankTransactionsPageKeys,
    TaxesCacheKeys.Taxes,
  ],
  priceItems: [
    RevenuesCacheKeys.PriceList,
    RevenuesCacheKeys.HasSavedPriceItems,
    RevenuesCacheKeys.RevenuesPriceItemsDropdown,
  ],
  peppolIntegration: [IntegrationsCacheKeys.PeppolIntegration],
  userVerification: [UserCacheKeys.Verifications],
  exportExpenseCompleted: [
    ExpensesCacheKeys.AllExpenses,
    ExpensesCacheKeys.ExpensesToReview,
  ],
  exportRevenueCompleted: [
    RevenuesCacheKeys.AllRevenues,
    RevenuesCacheKeys.RevenuesToReview,
  ],
  exportTransactionCompleted: [BankTransactionsCacheKeys.TransactionsList],
};

type ResourceKeyType = keyof typeof mapper;

type ResourceConfig =
  | ResourceKeyType
  | {
      method: 'remove';
      resource: ResourceKeyType;
      filters?: QueryFilters;
      options?: undefined;
    }
  | {
      method: 'invalidate';
      resource: ResourceKeyType;
      filters?: InvalidateQueryFilters;
      options?: InvalidateOptions;
    };

const useInvalidateResources = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (config: ResourceConfig) => {
      if (!config) return;

      const args =
        typeof config === 'string'
          ? {
              method: 'invalidate',
              resource: config as ResourceKeyType,
              filters: undefined,
              options: undefined,
            }
          : config;

      if (!args.resource || !args.method) return;

      const resource = mapper[args.resource];

      if (!resource) return;

      resource.forEach((key) => {
        if (args.method === 'invalidate') {
          queryClient.invalidateQueries(
            {
              ...args.filters,
              predicate: (query) => query.queryKey.includes(key),
            },
            args.options,
          );
        }
        if (args.method === 'remove') {
          queryClient.removeQueries({
            ...args.filters,
            predicate: (query) => query.queryKey.includes(key),
          });
        }
      });
    },
    [queryClient],
  );
};

export default useInvalidateResources;
